import { QAndAContentData } from '../../FlyOutUsageMetering/RightSectionComponents/QAndASidePannel'

type SectionName =
  | 'prerequisites'
  | 'accountConfiguration'
  | 'cloudCredentials'
  | 'companyDetails'

interface FaqData {
  content: QAndAContentData[]
}
export const FlyoutCloudSettingsFaqData: Record<SectionName, FaqData> = {
  prerequisites: {
    content: [
      {
        question:
          'Q1. Why do I need to complete prerequisites before using Labra?',
        answer:
          'These steps ensure your Microsoft Partner Center account is ready for marketplace activity. Without them, Labra cannot connect to manage your listings, offers, or revenue.',
      },
      {
        question: 'Q2 Who can complete these prerequisites?',
        answer:
          'Only a Partner Center global admin or account admin can complete enrollment steps like Commercial Marketplace setup or Payout & Tax profile. If you don’t have these permissions, ask your organization’s admin.',
      },
      {
        question: 'Q3. Do I need a Partner Center account?',
        answer:
          'Yes. A Partner Center account is required to access Microsoft’s marketplace programs.',
      },
      {
        question:
          'Q4. What’s the difference between Microsoft Cloud Partner Program and Commercial Marketplace?',
        answer: (
          <ul
            style={{
              marginLeft: '-24px',
              marginTop: '4px',
            }}
          >
            <li>
              The Cloud Partner Program makes you an official Microsoft partner.
            </li>
            <li>
              The Commercial Marketplace program (enrolled within Partner
              Center) enables you to publish and sell offers on Azure
              Marketplace or AppSource.
            </li>
          </ul>
        ),
      },
      {
        question: 'Q5. Do I need to enroll in both programs?',
        answer:
          'Yes. You must first join the Cloud Partner Program, then enroll in the Commercial Marketplace.',
      },
      {
        question: 'Q6. Why do I need to complete my Organization Profile?',
        answer:
          'Microsoft requires your company’s legal details for verification. This may include your business name, address, and DUNS ID.',
      },
      {
        question: 'Q7. How long does Microsoft verification take?',
        answer:
          'Typically 1–5 business days, depending on how quickly Microsoft can verify your legal and tax details. Delays may occur if information doesn’t match official records.',
      },
      {
        question: 'Q8. Do I need a DUNS number?',
        answer:
          'If your company is registered with Dun & Bradstreet, Microsoft may use your DUNS ID for faster verification. If you don’t have one, you can still complete the setup by entering company details manually.',
      },
      {
        question:
          'Q9. Can I start Labra onboarding before completing all prerequisites?',
        answer:
          'No. Labra requires a fully active Partner Center setup to fetch and manage data.',
      },
      {
        question: 'Q10. Why do I need to set up Payout & Tax details?',
        answer:
          'This step ensures Microsoft can validate your tax information and disburse payments for marketplace sales.',
      },
      {
        question: 'Q11. What if my Payout or Tax profile is incomplete?',
        answer:
          'Microsoft won’t disburse revenue until these details are complete. Labra can connect but will show limited or no revenue data until payout setup is finalized.',
      },
      {
        question: 'Q12. What happens if I skip all these steps?',
        answer: (
          <>
            If prerequisites are incomplete in Partner Center:
            <ul
              style={{
                marginLeft: '-24px',
                marginTop: '4px',
              }}
            >
              <li>Microsoft won’t allow you to publish offers or get paid.</li>
              <li>
                Labra won’t be able to connect to your Partner Center, so you
                won’t see listings, offers, or revenue data inside the app.
              </li>
            </ul>
          </>
        ),
      },
      {
        question: 'Q13. Can I edit or update these later?',
        answer:
          'Yes. Both Organization Profile and Payout & Tax details can be updated anytime in the Partner Center.',
      },
      {
        question: 'Q14. Do these steps give Labra access to my account?',
        answer:
          'No. These steps only complete your Microsoft setup. Labra access is granted later when you add permissions separately.',
      },
    ],
  },
  accountConfiguration: {
    content: [
      {
        question: 'Q1. What’s the difference between ISV and CP?',
        answer: (
          <ul
            style={{
              marginLeft: '-24px',
              marginTop: '4px',
            }}
          >
            <li>
              <span style={{ fontWeight: 500 }}>
                Independent Software Vendor (ISV):
              </span>{' '}
              You build and sell your own products directly to customers in the
              Microsoft marketplace.
            </li>
            <li>
              <span style={{ fontWeight: 500 }}>Channel Partner (CP):</span> You
              resell or distribute software on behalf of an ISV in the Microsoft
              marketplace.
            </li>
          </ul>
        ),
      },
      {
        question: 'Q2. Do I need to select ISV or CP manually?',
        answer:
          'No. Your account type (ISV or CP) is already configured by default when you onboard Labra.',
      },
      {
        question: 'Q3. Why do I need to select a currency?',
        answer:
          'Microsoft requires a transaction currency for your offers. All transactions will be processed, and payments disbursed, in the currency you select.',
      },
      {
        question: 'Q4. Can I select multiple currencies?',
        answer:
          'Yes. You can enable more than one currency, but only currencies that are fully set up in your Partner Center account will work.',
      },
      {
        question: 'Q5. What happens if I choose the wrong currency?',
        answer:
          'The currency selected here determines how you receive payouts. If it doesn’t match your Partner Center setup, your offers may fail during creation.',
      },
      {
        question: 'Q6. Is selecting additional currencies mandatory?',
        answer:
          'No. You only need to select the currencies you plan to transact in. Additional currencies are optional.',
      },
    ],
  },
  cloudCredentials: {
    content: [
      {
        question: 'Q1. Why do I need to connect my Microsoft cloud account?',
        answer:
          'Connecting your account allows Labra to access your Partner Center through secure APIs. This enables us to manage listings, offers, and revenue data on your behalf.',
      },
      {
        question: 'Q2. What details do I need to provide?',
        answer: (
          <>
            You’ll need:
            <ul
              style={{
                marginLeft: '-24px',
                marginTop: '4px',
              }}
            >
              <li>
                <span style={{ fontWeight: 500 }}>Tenant ID –</span> your Azure
                Active Directory (AD) tenant unique identifier.
              </li>
              <li>
                <span style={{ fontWeight: 500 }}>Client ID –</span> The ID
                assigned when you register an application in Azure AD. It
                identifies the app Labra will use to connect.
              </li>
              <li>
                <span style={{ fontWeight: 500 }}>Client Secret –</span>A secure
                password generated for the registered app. Labra uses it (along
                with the Client ID) to authenticate API calls.
              </li>
            </ul>
          </>
        ),
      },
      {
        question: 'Q3. Where can I find these details?',
        answer: (
          <ul
            style={{
              marginLeft: '-24px',
              marginTop: '4px',
            }}
          >
            <li>
              Tenant ID and Client ID are available under App Registrations in
              the Azure portal.
            </li>
            <li>
              Client Secret must be generated when creating or editing the App
              Registration.
            </li>
          </ul>
        ),
      },
      {
        question: 'Q4. Where can I find my Tenant ID?',
        answer: (
          <ul
            style={{
              marginLeft: '-24px',
              marginTop: '4px',
            }}
          >
            <li>
              Log in to the{' '}
              <a
                href="https://portal.azure.com"
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  textDecoration: 'none',
                  color: '#1942C5',
                  fontWeight: 500,
                }}
              >
                Azure Portal
              </a>
              .
            </li>
            <li>Navigate to Azure Active Directory.</li>
            <li>Under Overview, copy the Tenant ID listed.</li>
          </ul>
        ),
      },
      {
        question: 'Q5. How do I get my Client ID?',
        answer: (
          <ul
            style={{
              marginLeft: '-24px',
              marginTop: '4px',
            }}
          >
            <li>
              In the{' '}
              <a
                href="https://portal.azure.com"
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  textDecoration: 'none',
                  color: '#1942C5',
                  fontWeight: 500,
                }}
              >
                Azure Portal
              </a>
              , go to Azure Active Directory.
            </li>
            <li>Select App registrations {'>'} choose your registered app.</li>
            <li>Under Overview, copy the Application (client) ID.</li>
          </ul>
        ),
      },
      {
        question: 'Q6. How do I create a Client Secret?',
        answer: (
          <ul
            style={{
              marginLeft: '-24px',
              marginTop: '4px',
            }}
          >
            <li>
              Go to your app in{' '}
              <span style={{ fontWeight: 500 }}>App registrations</span>.
            </li>
            <li>
              In the left menu, select{' '}
              <span style={{ fontWeight: 500 }}>Certificates & secrets</span>.
            </li>
            <li>
              Click <span style={{ fontWeight: 500 }}>New client secret</span>
              give it a name, set an expiry, and click{' '}
              <span style={{ fontWeight: 500 }}>Add</span>.
            </li>
            <li>
              Copy the generated value and paste it into Labra. (Important: You
              won’t be able to view it again once you leave the page.)
            </li>
          </ul>
        ),
      },
      {
        question: 'Q7. Is my Client Secret safe with Labra?',
        answer:
          'Yes. The Client Secret is encrypted and stored securely. It’s only used to authenticate Labra’s API calls to Microsoft on your behalf.',
      },
      {
        question:
          'Q8. What if I accidentally close the page without saving the Client Secret?',
        answer:
          'You’ll need to create a new Client Secret in Azure. Microsoft does not allow you to view the old one again.',
      },
      {
        question:
          'Q9. Can I use the same credentials for multiple connections?',
        answer:
          'No. Each cloud account connection should have its own Tenant ID, Client ID, and Client Secret for proper tracking and management.',
      },

      {
        question: 'Q10. What if my Client Secret expires?',
        answer:
          'You’ll need to generate a new Client Secret in the Azure portal and update it in Labra to maintain your connection.',
      },

      {
        question: 'Q11. Can I update or remove my cloud account later?',
        answer:
          'Yes. You can always edit, replace, or remove a cloud account connection from Labra’s settings.',
      },

      {
        question: 'Q12. Do I need to give admin consent for API permissions?',
        answer:
          'Yes. Admin consent ensures Labra can access your Partner Center without prompting for login every time. Without it, certain actions (like creating private offers) will fail.',
      },

      {
        question:
          'Q13. Can I use an existing App Registration instead of creating a new one?',
        answer:
          'Yes, but only if the existing app has the correct API permissions enabled. If not, you’ll need to adjust permissions or create a new registration.',
      },
      {
        question:
          'Q14. Why is Labra asking for both programmatic access and a human user?',
        answer:
          'Programmatic access (Tenant ID, Client ID, Client Secret) is needed for API integrations. Adding a Labra user to Partner Center helps in cases where manual listing management or troubleshooting is required.',
      },
      {
        question: 'Q15. What happens if I enter the wrong credentials?',
        answer:
          'The connection will fail, and you’ll see an error message. Double-check the Tenant ID, Client ID, and Client Secret for typos.',
      },

      {
        question: 'Q16. Can I connect multiple cloud accounts?',
        answer:
          'Yes. If your organization manages multiple tenants or publisher accounts, you can add them separately in Labra.',
      },

      {
        question: 'Q17. Who should set this up?',
        answer:
          'A Microsoft Partner Center global admin or Azure AD admin should perform this setup to ensure the right permissions are granted.',
      },
      {
        question: 'Q18. What if I don’t have Azure admin access?',
        answer:
          'You’ll need to work with your organization’s global admin to generate the credentials and then enter them into Labra.',
      },
    ],
  },
  companyDetails: {
    content: [
      {
        question: 'Q1. Why does Labra need my company details?',
        answer:
          'These details are stored within Labra to personalize your workspace, organize listings, and give your team a consistent view of your company profile across the app.',
      },
      {
        question: 'Q2. Is this connected to Microsoft Partner Center?',
        answer:
          'No. The information here is for Labra’s internal use only. It does not affect your Microsoft Partner Center setup or verification.',
      },
      {
        question: 'Q3. Do I have to use my legal company name?',
        answer:
          'Not necessarily. You can use your brand name or any identifier your team will recognize. This will only be visible inside Labra.',
      },
      {
        question: 'Q4. Is a website required?',
        answer: 'Yes, entering a website is mandatory.',
      },

      {
        question: 'Q5. Which industry should I choose?',
        answer:
          'Industry selection is mainly for internal tagging and reporting inside Labra. If you don’t find an exact match, pick the closest one, it won’t affect your Microsoft onboarding.',
      },

      {
        question: 'Q6. Do I need to accept Labra’s Terms and Conditions?',
        answer:
          'Yes. Accepting Labra’s Terms and Conditions is mandatory to proceed with onboarding. This ensures you’re aligned with how Labra operates and provides its services.',
      },

      {
        question: 'Q7. Can I edit company details later?',
        answer:
          'Yes. All company details you add here are saved automatically and can be updated anytime under settings.',
      },
    ],
  },
}
